import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  UserIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  PhoneIcon,
  CalendarIcon,
  AwardIcon,
  HeartIcon,
  ShieldIcon
} from "lucide-react";
import { doctorService } from "../../services";
import { mockData } from "../../data/mock";
import { toast } from "sonner";
import { openWhatsApp } from "../../lib/utils";

const SpecialtyPage = () => {
  const { specialtyName } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    city: "",
    treatment: ""
  });
  const [specialty, setSpecialty] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch specialty data and doctors from API
  useEffect(() => {
    const fetchSpecialtyData = async () => {
      try {
        setLoading(true);
        const [specialtyInfo, doctorsData] = await Promise.all([
          // Create specialty info from specialtyName
          Promise.resolve({
            name: specialtyName,
            title: specialtyName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            description: `Expert ${specialtyName.replace(/-/g, ' ')} services with advanced treatment options.`,
            icon: UserIcon,
            color: 'bg-blue-500'
          }),
          doctorService.getDoctorsBySpecialty(specialtyName)
        ]);
        setSpecialty(specialtyInfo);
        setDoctors(doctorsData);
      } catch (err) {
        console.error('Error fetching specialty data:', err);
        setError('Failed to load specialty information');
        // Fallback to mock data
        const mockSpecialty = mockData.specialties.find(s => 
          s.name.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '') === specialtyName
        );
        setSpecialty(mockSpecialty);
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSpecialtyData();
  }, [specialtyName]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading specialty information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!specialty) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Specialty Not Found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleBooking = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      toast.error("Please fill required fields");
      return;
    }
    toast.success("Consultation booked successfully! Our team will contact you soon.");
    setFormData({ name: "", mobile: "", city: "", treatment: "" });
  };

  // Sample doctors for this specialty
  const specialtyDoctors = [
    {
      name: "Dr. Rajesh Kumar",
      experience: "15+ years",
      rating: 4.8,
      reviews: 234,
      location: "Delhi",
      image: "doctor1"
    },
    {
      name: "Dr. Priya Sharma", 
      experience: "12+ years",
      rating: 4.9,
      reviews: 189,
      location: "Mumbai",
      image: "doctor2"
    },
    {
      name: "Dr. Amit Singh",
      experience: "18+ years", 
      rating: 4.7,
      reviews: 156,
      location: "Bangalore",
      image: "doctor3"
    }
  ];

  const benefits = [
    "Minimally invasive procedures",
    "Faster recovery time",
    "Reduced post-operative pain",
    "Smaller surgical scars",
    "Same-day discharge (when applicable)",
    "24/7 post-operative support"
  ];

  const procedures = specialty.treatments || [];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            <div>
              <Badge className="mb-3 sm:mb-4">{specialty.name}</Badge>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                {specialty.name} Treatment
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                {specialty.description} Get advanced treatment from experienced specialists 
                with world-class care and support throughout your journey.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button size="lg" className="bg-gray-900 hover:bg-gray-600 text-sm sm:text-base" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from TheLivCure site.")}>
                  <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="hidden xs:inline">Call: +91 93117 20045</span>
                  <span className="xs:hidden">Call Now</span>
                </Button>
                <Button size="lg" variant="outline" className="text-sm sm:text-base">
                  <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  <span className="hidden sm:inline">Book Free Consultation</span>
                  <span className="sm:hidden">Book Consultation</span>
                </Button>
              </div>
            </div>

            {/* Booking Form */}
            <Card className="shadow-xl mt-8 lg:mt-0">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                  Book Free Consultation
                </h3>
                <form onSubmit={handleBooking} className="space-y-3 sm:space-y-4">
                  <Input
                    placeholder="Patient Name *"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="text-sm sm:text-base"
                  />
                  <Input
                    placeholder="Mobile Number *"
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    required
                    className="text-sm sm:text-base"
                  />
                  <Input
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="text-sm sm:text-base"
                  />
                  <Input
                    placeholder="Treatment Needed"
                    value={formData.treatment}
                    onChange={(e) => setFormData({...formData, treatment: e.target.value})}
                    className="text-sm sm:text-base"
                  />
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 py-2.5 sm:py-3 text-sm sm:text-base">
                    Book Now
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Procedures */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {specialty.name} Procedures We Offer
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive treatment options for all your medical needs
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {procedures.map((procedure, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircleIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{procedure}</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Advanced treatment using minimally invasive techniques for faster recovery
                  </p>
                  <Button variant="outline" size="sm" className="w-full">
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                Why Choose TheLivCure for {specialty.name}?
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mt-8 lg:mt-0">
              <Card className="text-center">
                <CardContent className="p-3 sm:p-4 lg:p-6">
                  <HeartIcon className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2 sm:mb-3" />
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">99%</div>
                  <div className="text-xs sm:text-sm text-gray-600">Success Rate</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-3 sm:p-4 lg:p-6">
                  <ClockIcon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 mx-auto mb-2 sm:mb-3" />
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">30 min</div>
                  <div className="text-xs sm:text-sm text-gray-600">Average Procedure</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-3 sm:p-4 lg:p-6">
                  <ShieldIcon className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mx-auto mb-2 sm:mb-3" />
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">24/7</div>
                  <div className="text-xs sm:text-sm text-gray-600">Support Available</div>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-3 sm:p-4 lg:p-6">
                  <AwardIcon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 mx-auto mb-2 sm:mb-3" />
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-xs sm:text-sm text-gray-600">All Insurance Covered</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Doctors */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Expert {specialty.name} Doctors
            </h2>
            <p className="text-lg text-gray-600">
              Experienced specialists dedicated to your health and recovery
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {specialtyDoctors.map((doctor, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <UserIcon className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{doctor.name}</h3>
                  <p className="text-blue-600 font-medium mb-3 text-sm sm:text-base">{specialty.name} Specialist</p>
                  
                  <div className="space-y-2 mb-4">
                    {/* Ratings/reviews temporarily disabled */}
                    {false && (
                    <div className="flex items-center justify-center space-x-1">
                      <StarIcon className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">{doctor.rating}</span>
                      <span className="text-sm text-gray-500">({doctor.reviews} reviews)</span>
                    </div>
                    )}
                    <p className="text-sm text-gray-600">{doctor.experience}</p>
                    <p className="text-sm text-gray-600">{doctor.location}</p>
                  </div>
                  
                  <div className="flex flex-col xs:flex-row gap-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs sm:text-sm">
                      View Profile
                    </Button>
                    <Button size="sm" className="flex-1 bg-gray-900 hover:bg-gray-600 text-xs sm:text-sm">
                      Book Appointment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Testimonials temporarily disabled */}
      {false && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Patients Say</h2>
            <p className="text-lg text-gray-600">Real experiences from patients who underwent {specialty.name} treatment</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((testimonial) => (
              <Card key={testimonial}>
                <CardContent className="p-6">...</CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about {specialty.name} treatment
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: `What is ${specialty.name} treatment?`,
                answer: `${specialty.name} treatment involves advanced medical procedures to address specific conditions. Our specialists use minimally invasive techniques for better outcomes.`
              },
              {
                question: "How long does the recovery take?",
                answer: "Recovery time varies by procedure, but most patients can return to normal activities within 1-2 weeks with proper care and follow-up."
              },
              {
                question: "Is the treatment covered by insurance?",
                answer: "Yes, most procedures are covered by insurance. We work with all major insurance providers and offer cashless treatment options."
              },
              {
                question: "Are there any risks involved?",
                answer: "All medical procedures carry some risks, but our experienced doctors use advanced techniques to minimize complications. We discuss all risks during consultation."
              }
            ].map((faq, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready for Your {specialty.name} Treatment?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Book a free consultation with our expert doctors and take the first step towards better health
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gray-900 hover:bg-gray-600" onClick={() => openWhatsApp("9311720045", "Hi, I am contacting you for treatment from TheLivCure site.")}>
              <PhoneIcon className="w-5 h-5 mr-2" />
              Call Now: +91 93117 20045
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              <CalendarIcon className="w-5 h-5 mr-2" />
              Book Online Consultation
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SpecialtyPage;